/**
 * 历史记录模态框组件
 *
 * 用于 Widget 中展示和管理历史对话
 *
 * 设计规范：
 * - 全屏覆盖 Widget（380 × 640px）
 * - 半透明遮罩层
 * - 白色内容区
 * - 历史记录列表（可滚动）
 */

import React from 'react';
import { clsx } from 'clsx';
import type { Conversation } from '../../services/types';
import { formatRelativeTime } from '@/utils/date';

export interface HistoryModalProps {
  /** 是否打开 */
  isOpen: boolean;
  /** 会话列表 */
  sessions: Conversation[];
  /** 当前会话 ID */
  currentSessionId: string | null;
  /** 是否加载中 */
  isLoading: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 选择会话回调 */
  onSelectSession: (id: string) => void;
  /** 关闭会话按钮点击回调 */
  onCloseSessionClick: (id: string) => void;
  /** 自定义 CSS 类名 */
  className?: string;
}


/**
 * 截断标题（超过 20 字符显示省略号）
 */
function truncateTitle(title: string, maxLength: number = 20): string {
  if (title.length <= maxLength) return title;
  return title.slice(0, maxLength) + '...';
}

export const HistoryModal: React.FC<HistoryModalProps> = ({
  isOpen,
  sessions,
  currentSessionId,
  isLoading,
  onClose,
  onSelectSession,
  onCloseSessionClick,
  className,
}) => {
  // ESC 键关闭
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onClose]);

  // 阻止背景滚动
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className={clsx(
        'fixed inset-0 z-50 flex items-center justify-center p-4',
        className
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="history-modal-title"
    >
      {/* 半透明遮罩 */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={isLoading ? undefined : onClose}
        aria-hidden="true"
      />

      {/* 模态框内容 */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[380px] max-h-[640px] flex flex-col animate-in fade-in zoom-in duration-200">
        {/* 头部 */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2
            id="history-modal-title"
            className="text-lg font-semibold text-gray-900"
          >
            历史对话
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className={clsx(
              'p-1.5 rounded-lg transition-all duration-200',
              'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            aria-label="关闭"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 内容区 */}
        <div className="flex-1 overflow-y-auto p-2">
          {isLoading ? (
            // 加载状态
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <svg
                  className="animate-spin h-8 w-8 text-blue-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="text-sm text-gray-500">加载中...</span>
              </div>
            </div>
          ) : sessions.length === 0 ? (
            // 空状态
            <div className="flex flex-col items-center justify-center py-12 px-4">
              {/* 空状态图标 */}
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="text-sm text-gray-500 text-center">
                暂无历史对话
              </p>
              <p className="text-xs text-gray-400 text-center mt-1">
                开始对话后将自动保存
              </p>
            </div>
          ) : (
            // 会话列表
            <ul className="space-y-1" role="listbox">
              {sessions.map((session) => {
                const isCurrent = session.id === currentSessionId;

                return (
                  <li
                    key={session.id}
                    role="option"
                    aria-selected={isCurrent}
                  >
                    <button
                      type="button"
                      onClick={() => onSelectSession(session.id)}
                      className={clsx(
                        'w-full flex items-center gap-3 px-4 py-3 rounded-xl',
                        'transition-all duration-200',
                        'text-left group',
                        isCurrent
                          ? 'bg-blue-50 border border-blue-200'
                          : 'hover:bg-gray-50 border border-transparent'
                      )}
                    >
                      {/* 会话图标 */}
                      <div
                        className={clsx(
                          'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
                          isCurrent
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-500'
                        )}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                          />
                        </svg>
                      </div>

                      {/* 会话信息 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p
                            className={clsx(
                              'text-sm font-medium truncate',
                              isCurrent ? 'text-blue-700' : 'text-gray-900'
                            )}
                          >
                            {truncateTitle(session.title)}
                          </p>
                          {isCurrent && (
                            <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-blue-500 text-white rounded-full">
                              当前
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {session.message_count} 条消息 · {formatRelativeTime(session.updated_at || session.created_at)}
                        </p>
                      </div>

                      {/* 关闭会话按钮 */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('确定要关闭此会话吗？')) {
                            onCloseSessionClick(session.id);
                          }
                        }}
                        className={clsx(
                          'flex-shrink-0 p-1.5 rounded-lg transition-all duration-200',
                          'text-gray-400 hover:text-red-500 hover:bg-red-50',
                          'opacity-0 group-hover:opacity-100',
                          'focus:opacity-100'
                        )}
                        aria-label={`关闭 ${session.title}`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* 底部提示 */}
        {sessions.length > 0 && !isLoading && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
            <p className="text-xs text-gray-500 text-center">
              点击对话可继续聊天
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
